import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  formatCrashDebugText,
  getBootDebugSnapshot,
  getLastCrash,
  getRecentErrorLogEntries
} from '../../infrastructure/telemetry/crashDebug';
import { RefreshCw, Copy, Clock } from 'lucide-react';
import '../../styles/storage-repair-modal.css';

function resolveWaitingKey({ context, roleReady, dataShellReady, dataReady, dataLoaded }) {
  if (context === 'auth') return 'waitingAuth';
  if (roleReady === false) return 'waitingRole';
  if (dataShellReady === false) return 'waitingShell';
  if (dataLoaded === false || dataReady === false) return 'waitingData';
  return 'waitingUnknown';
}

/**
 * Zeigt Bootstrap-Status wenn Loading zu lange dauert.
 * Nur warten / neu laden — keine Reparatur-/Konfigurieren-Aktion.
 */
export default function StorageBootstrapDiagnostic({
  context = 'app',
  dataReady,
  dataLoaded,
  dataShellReady,
  roleReady,
  businessId,
  title,
  hint,
  reloadLabel,
  onDismiss
}) {
  const { t } = useLanguage();
  const tb = (key) => t(`storageBootstrap.${key}`);

  const [copied, setCopied] = useState(false);

  const lastCrash = useMemo(() => getLastCrash(), []);
  const recentErrors = useMemo(() => getRecentErrorLogEntries(3), []);
  const bootSnap = useMemo(() => getBootDebugSnapshot(), []);

  const waitingKey = resolveWaitingKey({
    context,
    roleReady,
    dataShellReady,
    dataReady,
    dataLoaded
  });

  const reloadApp = () => {
    window.location.reload();
  };

  const copyDebug = async () => {
    const text = formatCrashDebugText({ includeBoot: true });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  const bootReport =
    typeof window !== 'undefined' && window.__AMAN_BOOT_TELEMETRY?.isEnabled?.()
      ? window.__AMAN_BOOT_TELEMETRY.export()
      : null;

  const boolLabel = (value) => (value ? tb('yes') : tb('no'));

  return (
    <div className="storage-bootstrap-diagnostic">
      <div className="storage-bootstrap-diagnostic__spinner" />
      <h2>{title || tb('title')}</h2>
      <p className="storage-bootstrap-diagnostic__hint">{hint || tb('hint')}</p>
      <p className="storage-bootstrap-diagnostic__hint">
        <strong>
          {tb('waitingFor')}: {tb(waitingKey)}
        </strong>
      </p>
      <p className="storage-bootstrap-diagnostic__hint storage-bootstrap-diagnostic__hint--muted">
        {tb('backgroundNote')}
      </p>
      <dl className="storage-bootstrap-diagnostic__grid">
        <dt>{tb('context')}</dt>
        <dd>{context}</dd>
        <dt>{tb('businessId')}</dt>
        <dd>{businessId || '—'}</dd>
        <dt>{tb('dataReady')}</dt>
        <dd>{boolLabel(dataReady)}</dd>
        <dt>{tb('dataLoaded')}</dt>
        <dd>{boolLabel(dataLoaded)}</dd>
        {dataShellReady != null ? (
          <>
            <dt>{tb('shellReady')}</dt>
            <dd>{boolLabel(dataShellReady)}</dd>
          </>
        ) : null}
        <dt>{tb('roleReady')}</dt>
        <dd>{boolLabel(roleReady)}</dd>
        {bootReport ? (
          <>
            <dt>{tb('bootMsDashboard')}</dt>
            <dd>{bootReport.summary?.msToDashboardMount ?? '—'}</dd>
            <dt>{tb('idbReadsWrites')}</dt>
            <dd>
              {bootReport.summary?.idbReads ?? 0} / {bootReport.summary?.idbWrites ?? 0}
            </dd>
            <dt>{tb('jsonParseStringify')}</dt>
            <dd>
              {bootReport.summary?.jsonParseCalls ?? 0} /{' '}
              {bootReport.summary?.jsonParseStringifyCalls ??
                bootReport.summary?.jsonStringifyCalls ??
                0}
            </dd>
          </>
        ) : bootSnap ? (
          <>
            <dt>{tb('lastBootGate')}</dt>
            <dd>{bootSnap.lastGate || '—'}</dd>
            <dt>{tb('bootMsDashboard')}</dt>
            <dd>{bootSnap.msToDashboard ?? '—'}</dd>
          </>
        ) : null}
      </dl>

      {(lastCrash?.message || recentErrors.length > 0) && (
        <pre
          className="storage-bootstrap-diagnostic__debug"
          style={{
            marginTop: 12,
            padding: 12,
            maxHeight: 180,
            overflow: 'auto',
            fontSize: 12,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            background: 'var(--ads-color-background, #f5f5f5)',
            borderRadius: 8
          }}
        >
          {lastCrash?.message
            ? `[Crash] ${lastCrash.name || 'Error'}: ${lastCrash.message}`
            : ''}
          {recentErrors.length
            ? `\n[error_log] ${recentErrors
                .map((e) => `${e.context || '?'}: ${e.error?.message || ''}`)
                .join('\n')}`
            : ''}
        </pre>
      )}

      <div className="storage-bootstrap-diagnostic__actions">
        {typeof onDismiss === 'function' ? (
          <button
            type="button"
            className="storage-repair-modal__btn storage-repair-modal__btn--primary"
            onClick={onDismiss}
          >
            <Clock size={16} />
            {tb('waitLabel')}
          </button>
        ) : null}
        <button
          type="button"
          className="storage-repair-modal__btn storage-repair-modal__btn--primary"
          onClick={reloadApp}
        >
          <RefreshCw size={16} />
          {reloadLabel || tb('reloadLabel')}
        </button>
        <button
          type="button"
          className="storage-repair-modal__btn storage-repair-modal__btn--ghost"
          onClick={() => void copyDebug()}
        >
          <Copy size={16} />
          {copied ? tb('copied') : tb('copyDebug')}
        </button>
      </div>
    </div>
  );
}
